import React, { useState } from 'react';
import { toast } from 'sonner';
import { jobsAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { DatePicker } from './DatePicker';
import {
  AlertTriangle,
  FilePlus2,
  Loader2,
  Plus,
  Receipt,
  Trash2,
} from 'lucide-react';

/**
 * Build the list of seed line items for the Final Invoice modal.
 * Prefers the imported PO snapshot, then the first/accepted quote, else a blank row.
 */
const buildSeedItems = (job, quotes) => {
  const snap = job?.imported_from_po;
  if (snap?.items?.length) {
    return snap.items.map((it) => ({
      description: it.description || '',
      quantity: parseFloat(it.quantity) || 1,
      unit: it.unit || 'Item',
      unit_price: parseFloat(it.unit_price) || 0,
      trade_type: it.trade_type || 'General',
      kind: 'standard',
      completed: true,
    }));
  }
  if (quotes?.length) {
    const q = quotes.find((x) => x.status === 'accepted') || quotes[0];
    const flat = [];
    (q.sections || []).forEach((s) => (s.items || []).forEach((it) => flat.push(it)));
    const items = flat.length ? flat : (q.items || []);
    if (items.length) {
      return items.map((it) => ({
        description: it.description || '',
        quantity: parseFloat(it.quantity) || 1,
        unit: it.unit || 'Item',
        unit_price: parseFloat(it.unit_price) || 0,
        trade_type: it.trade_type || 'General',
        kind: 'standard',
        completed: true,
      }));
    }
  }
  return [{ description: '', quantity: 1, unit: 'Item', unit_price: 0, kind: 'standard', completed: true }];
};

export const FinalInvoiceDialog = ({ open, onOpenChange, job, quotes, jobId, onSuccess }) => {
  const [mode, setMode] = useState('final'); // 'progress' | 'final'
  const [items, setItems] = useState([]);
  const [chargeGst, setChargeGst] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overPoOpen, setOverPoOpen] = useState(false);
  const [overPoDetail, setOverPoDetail] = useState(null);

  // Progress-mode state
  const [progressPct, setProgressPct] = useState(25);
  const [progressDescription, setProgressDescription] = useState('');

  // When the dialog opens, seed items from PO/quote and load the summary.
  React.useEffect(() => {
    if (!open) return;
    let active = true;
    setItems(buildSeedItems(job, quotes));
    setNotes('');
    setProgressPct(25);
    setProgressDescription('');
    setDueDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
    (async () => {
      try {
        const res = await jobsAPI.getInvoiceSummary(jobId);
        if (!active) return;
        setSummary(res.data);
        const prev = res.data?.previous_invoices?.[res.data.previous_invoices.length - 1];
        setChargeGst(prev && typeof prev.charge_gst === 'boolean' ? prev.charge_gst : true);
        // Default to Progress mode for new jobs that have a PO but no invoices yet
        const hasPo = !!res.data?.po_total;
        const hasPrior = (res.data?.previous_invoices || []).length > 0;
        setMode(hasPo && !hasPrior ? 'progress' : 'final');
      } catch {
        if (!active) return;
        setSummary(null);
        setChargeGst(true);
        setMode('final');
      }
    })();
    return () => {
      active = false;
    };
  }, [open, job, quotes, jobId]);

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      const numeric = field === 'quantity' || field === 'unit_price';
      next[idx] = { ...next[idx], [field]: numeric ? (parseFloat(value) || 0) : value };
      return next;
    });
  };

  const addItem = (kind = 'standard') => {
    const seed = kind === 'variation' ? 'Variation: ' : (kind === 'ps' ? 'PS adjustment: ' : (kind === 'pc' ? 'PC adjustment: ' : ''));
    setItems((prev) => [
      ...prev,
      { description: seed, quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General', kind, completed: true },
    ]);
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // Calculate totals depending on mode
  const totals = (() => {
    let subtotal = 0;
    if (mode === 'progress') {
      const poTotal = parseFloat(summary?.po_total) || 0;
      const pct = Math.max(0, Math.min(100, parseFloat(progressPct) || 0));
      subtotal = Math.round(poTotal * (pct / 100) * 100) / 100;
    } else {
      subtotal = items
        .filter((it) => it.completed && it.description)
        .reduce((sum, it) => sum + (parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0)), 0);
    }
    const tax = chargeGst ? subtotal * 0.1 : 0;
    return { subtotal, tax, total: subtotal + tax };
  })();

  const submit = async (acknowledgeOverPo) => {
    let body;
    if (mode === 'progress') {
      const pct = parseFloat(progressPct);
      if (!summary?.po_total) {
        toast.error('This job has no PO total. Switch to Final mode to invoice line items.');
        return;
      }
      if (!pct || pct <= 0 || pct > 100) {
        toast.error('Enter a percentage between 0 and 100');
        return;
      }
      body = {
        mode: 'progress',
        progress_pct: pct,
        progress_description: progressDescription || undefined,
        due_date: dueDate || undefined,
        notes: notes || undefined,
        override_gst: chargeGst,
        acknowledge_over_po: !!acknowledgeOverPo,
      };
    } else {
      const payloadItems = items
        .filter((it) => it.completed && it.description)
        .map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit || 'Item',
          unit_price: parseFloat(it.unit_price) || 0,
          trade_type: it.trade_type || 'General',
          kind: it.kind || 'standard',
        }));
      if (payloadItems.length === 0) {
        toast.error('Tick at least one completed line item');
        return;
      }
      body = {
        mode: 'final',
        items: payloadItems,
        due_date: dueDate || undefined,
        notes: notes || undefined,
        override_gst: chargeGst,
        acknowledge_over_po: !!acknowledgeOverPo,
      };
    }
    try {
      setLoading(true);
      await jobsAPI.generateFinalInvoice(jobId, body);
      toast.success(mode === 'progress' ? 'Progress invoice generated' : 'Final invoice generated');
      setOverPoOpen(false);
      setOverPoDetail(null);
      onOpenChange?.(false);
      onSuccess?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === 'exceeds_po') {
        setOverPoDetail(detail);
        setOverPoOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to generate invoice'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="final-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="h-5 w-5" /> Generate Invoice
            </DialogTitle>
            <DialogDescription>Issue a progress claim (% of the PO) or a final invoice from completed line items. The total updates live and is checked against the PO before saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Mode toggle */}
            <div className="inline-flex rounded-md border border-border p-1 bg-secondary/30" data-testid="invoice-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('progress')}
                className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${mode === 'progress' ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="invoice-mode-progress"
              >
                Progress claim
              </button>
              <button
                type="button"
                onClick={() => setMode('final')}
                className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${mode === 'final' ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="invoice-mode-final"
              >
                Final invoice
              </button>
            </div>

            {/* Summary banner */}
            {summary && (summary.po_total || summary.invoiced_to_date > 0) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded border border-border bg-secondary/30 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">PO Total</div>
                  <div className="font-mono font-bold">${(summary.po_total || 0).toFixed(2)}</div>
                  {summary.po_number && <div className="text-[11px] text-muted-foreground">{summary.po_number}</div>}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Invoiced to date</div>
                  <div className="font-mono font-bold">${(summary.invoiced_to_date || 0).toFixed(2)}</div>
                  <div className="text-[11px] text-muted-foreground">{summary.previous_invoices?.length || 0} prev invoice(s)</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Remaining on PO</div>
                  <div className={`font-mono font-bold ${(summary.remaining || 0) > 0 ? 'text-green-400' : 'text-orange-400'}`}>
                    ${(summary.remaining ?? 0).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">This invoice (live)</div>
                  <div className="font-mono font-bold text-primary" data-testid="final-invoice-live-total">
                    ${totals.total.toFixed(2)}
                  </div>
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-4 p-3 rounded border border-border">
              <div className="flex items-center gap-2">
                <Switch id="final-gst-toggle" checked={chargeGst} onCheckedChange={setChargeGst} data-testid="final-invoice-gst-toggle" />
                <Label htmlFor="final-gst-toggle" className="cursor-pointer">Apply GST (10%)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Due date</Label>
                <DatePicker value={dueDate} onChange={setDueDate} placeholder="Due date" data-testid="final-invoice-due-date" />
              </div>
              {mode === 'final' && (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => addItem('standard')} data-testid="final-invoice-add-item"><Plus className="h-3 w-3 mr-1" /> Item</Button>
                  <Button size="sm" variant="outline" onClick={() => addItem('variation')}><Plus className="h-3 w-3 mr-1" /> Variation</Button>
                  <Button size="sm" variant="outline" onClick={() => addItem('ps')}>+ PS</Button>
                  <Button size="sm" variant="outline" onClick={() => addItem('pc')}>+ PC</Button>
                </div>
              )}
            </div>

            {/* Mode-specific body */}
            {mode === 'progress' ? (
              <div className="space-y-4 p-4 rounded border border-border bg-secondary/20" data-testid="progress-mode-panel">
                {!summary?.po_total ? (
                  <div className="text-sm text-orange-400 bg-orange-500/10 border border-orange-500/30 rounded-sm p-3">
                    This job has no PO total captured. Switch to <strong>Final invoice</strong> mode to bill line items, or import the job from a PO first.
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-sm mb-2 block">Percentage of PO</Label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {[10, 25, 33, 50, 75, 100].map((p) => (
                          <Button
                            key={p}
                            type="button"
                            variant={Number(progressPct) === p ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setProgressPct(p)}
                            data-testid={`progress-pct-preset-${p}`}
                          >
                            {p}%
                          </Button>
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={progressPct}
                          onChange={(e) => setProgressPct(e.target.value)}
                          className="w-32"
                          data-testid="progress-pct-input"
                        />
                        <span className="text-sm text-muted-foreground">
                          of ${(summary.po_total || 0).toFixed(2)} PO ={' '}
                          <span className="font-mono font-bold text-primary" data-testid="progress-amount-preview">
                            ${totals.subtotal.toFixed(2)}
                          </span>
                          {' '}+ GST ${totals.tax.toFixed(2)} = ${totals.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Line description (optional)</Label>
                      <Input
                        value={progressDescription}
                        onChange={(e) => setProgressDescription(e.target.value)}
                        placeholder={`Progress claim — ${progressPct}% of ${summary.po_number || 'PO'}`}
                        data-testid="progress-description-input"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Items table */}
                <div className="border border-border rounded">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground bg-secondary/30">
                    <div className="col-span-1">Done</div>
                    <div className="col-span-5">Description</div>
                    <div className="col-span-1">Qty</div>
                    <div className="col-span-1">Unit</div>
                    <div className="col-span-2">Unit price</div>
                    <div className="col-span-2 text-right">Line total</div>
                  </div>
                  <div className="divide-y divide-border">
                    {items.map((it, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-2" data-testid={`final-invoice-row-${idx}`}>
                        <div className="col-span-1 flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={!!it.completed}
                            onChange={(e) => updateItem(idx, 'completed', e.target.checked)}
                            className="h-4 w-4 accent-green-500"
                            data-testid={`final-invoice-completed-${idx}`}
                          />
                        </div>
                        <div className="col-span-5">
                          <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Line item description" className="text-sm" />
                          {it.kind && it.kind !== 'standard' && (
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.kind}</span>
                          )}
                        </div>
                        <div className="col-span-1">
                          <Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="text-sm" />
                        </div>
                        <div className="col-span-1">
                          <Input value={it.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} className="text-sm" />
                        </div>
                        <div className="col-span-2">
                          <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} className="text-sm" />
                        </div>
                        <div className="col-span-1 text-right font-mono text-sm">
                          ${((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)).toFixed(2)}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} className="h-7 w-7">
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-72 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">${totals.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>GST (10%)</span><span className="font-mono">${totals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold border-t border-border pt-1"><span>Total</span><span className="font-mono" data-testid="final-invoice-total">${totals.total.toFixed(2)}</span></div>
              </div>
            </div>

            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes (payment terms, bank details, etc.)" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={loading}>Cancel</Button>
            <Button
              onClick={() => submit(false)}
              disabled={loading || (mode === 'progress' && !summary?.po_total)}
              data-testid="final-invoice-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Receipt className="h-4 w-4 mr-1" />}
              {mode === 'progress' ? 'Generate Progress Invoice' : 'Generate Final Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Over-PO warning */}
      <Dialog open={overPoOpen} onOpenChange={setOverPoOpen}>
        <DialogContent className="bg-card border-orange-500/50 max-w-lg" data-testid="over-po-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <AlertTriangle className="h-5 w-5" /> Invoice exceeds PO total
            </DialogTitle>
            <DialogDescription>This invoice plus prior invoices on this job will exceed the original PO amount. Review the figures below before issuing.</DialogDescription>
          </DialogHeader>
          {overPoDetail && (
            <div className="space-y-3 text-sm">
              <p>{overPoDetail.message}</p>
              <div className="grid grid-cols-2 gap-2 p-3 rounded border border-border bg-secondary/30">
                <div>
                  <div className="text-xs text-muted-foreground">PO total</div>
                  <div className="font-mono font-bold">${(overPoDetail.po_total || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Already invoiced</div>
                  <div className="font-mono font-bold">${(overPoDetail.invoiced_to_date || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">This invoice</div>
                  <div className="font-mono font-bold">${(overPoDetail.this_invoice || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Cumulative</div>
                  <div className="font-mono font-bold text-orange-400">${(overPoDetail.cumulative || 0).toFixed(2)}</div>
                </div>
              </div>
              {overPoDetail.previous_invoice_numbers?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Previous invoices: {overPoDetail.previous_invoice_numbers.join(', ')}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Confirming will issue this invoice anyway (e.g. approved variations). The over-PO amount will be recorded for reporting.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverPoOpen(false)} disabled={loading} data-testid="over-po-cancel">Cancel</Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => submit(true)}
              disabled={loading}
              data-testid="over-po-confirm"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
              Issue anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinalInvoiceDialog;
